import type { TimeOnSiteSummary } from "#/types/report";
import { Table } from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ReportSection from "./ReportSection";

interface TimeOnSiteSectionProps {
  data: TimeOnSiteSummary;
  tooltip?: ReactNode;
}

const formatDays = (value: number | null, fallback: string): string =>
  value === null ? fallback : `${value} ${value === 1 ? "day" : "days"}`;

const TimeOnSiteSection = ({ data, tooltip }: TimeOnSiteSectionProps) => {
  const { t } = useTranslation();

  return (
    <ReportSection
      title={t("reports.inventoryScan.timeOnSite.title")}
      subtitle={
        <>
          <span>
            {t("reports.inventoryScan.timeOnSite.subtitle", {
              average: formatDays(data.averageDays, t("reports.inventoryScan.timeOnSite.noData")),
              count: data.totalStays,
            })}
          </span>
          <span>{t("reports.inventoryScan.timeOnSite.definition")}</span>
        </>
      }
      tooltip={tooltip}
    >
      {data.byProduct.length > 0 ? (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("reports.inventoryScan.timeOnSite.category")}</Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.timeOnSite.averageTime")}
              </Table.Th>
              <Table.Th className="text-right">
                {t("reports.inventoryScan.timeOnSite.stays")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.byProduct.map((entry) => (
              <Table.Tr key={entry.productId ?? entry.productName ?? "unknown"}>
                <Table.Td className="font-medium">
                  {entry.productName ?? t("reports.inventoryScan.timeOnSite.noCategory")}
                </Table.Td>
                <Table.Td className="text-right">
                  {formatDays(entry.averageDays, "--")}
                </Table.Td>
                <Table.Td className="text-right">{entry.stayCount}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <span className="text-sm text-gray-500">
          {t("reports.inventoryScan.timeOnSite.noItems")}
        </span>
      )}
    </ReportSection>
  );
};

export default TimeOnSiteSection;
